import {Skeleton} from "@mui/material";
import Typography from "@mui/material/Typography";
import {ReactElement} from "react";
import {ElementApiClient} from "../../../../../../common/apis/element/ElementApiClient";
import {ElementDto} from "../../../../../../common/apis/element/ElementDto";
import {ElementFilterDto} from "../../../../../../common/apis/element/ElementFilterDto";
import GraphApiClient from "../../../../../../common/apis/GraphApiClient";
import {RelationshipApiClient} from "../../../../../../common/apis/relationship/RelationshipApiClient";
import {ReferenceDefinitionDto} from "../../../../../../common/apis/stereotype/ReferenceDefinitionDto";
import MultiSelectField from "../../../../../../components/fields/select/MultiSelectField";
import {t} from "../../../../../../store/localization/TranslMessasge";
import {CommonTranslationKey} from "../../../CommonTranslationKey";
import ReferenceDefinitionGraphSearcher from "../../reference/ReferenceDefinitionGraphSearcher";
import {StereotypesTranslationKey} from "../../StereotypesTranslationKey";
import {SMART_REFERENCE_LIST_FIELD_COMPONENT_TYPE, SmartReferenceListField} from "../model/SmartReferenceListField";
import {UiComponent} from "../model/UiComponent";
import {
    UiComponentDataContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentRenderingProps,
    UiComponentStateContext,
    UiComponentValidationContext
} from "../UiComponentRendererPlugin";
import ErrorFieldPlaceholder from "./ErrorFieldPlaceholder";
import {SmartReferenceSelectFieldPlugin} from "./SmartReferenceSelectFieldPlugin";


interface RelationData {
    relationshipId: string;
    relatedElement: ElementDto;
}

export interface SmartReferenceListFieldState {
    originalRelations: RelationData[];
    relatedElements: ElementDto[];
    elements: ElementDto[];
}

export class SmartReferenceListFieldPlugin implements UiComponentRendererPlugin {

    private readonly graphApiClient = new GraphApiClient();
    private readonly elementApiClient = new ElementApiClient();
    private readonly relationshipApiClient = new RelationshipApiClient();

    getSupportedComponentTypes(): string[] {
        return [SMART_REFERENCE_LIST_FIELD_COMPONENT_TYPE];
    }

    getInitialValues(component: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const smartReferenceField = component as SmartReferenceListField;

        const referenceDefinition: ReferenceDefinitionDto = dataContext.referenceDefinitions[smartReferenceField.referenceDefinitionId];
        if (!referenceDefinition) {
            return Promise.resolve({});
        }

        const relationshipDetailsPromise = this.graphApiClient.findNeighbours([dataContext.element.identifier])
                .then(graph => {
                    return ReferenceDefinitionGraphSearcher.findRelatedElementsByReferenceDefinition(dataContext.element, referenceDefinition, graph);
                });

        const elementFilter: ElementFilterDto = {
            types: [referenceDefinition.relatedElementFilter.type],
            stereotypeId: referenceDefinition.relatedElementFilter.stereotypeId ?? undefined
        };
        const filteredElementsPromise = this.elementApiClient.findByFilter(elementFilter)
                .then(resultList => resultList.items);

        return Promise.all([relationshipDetailsPromise, filteredElementsPromise])
                .then(([relationshipDetails, filteredElements]) => {
                    const relations = relationshipDetails.map(relationshipDetail => ({
                        relationshipId: relationshipDetail.relationship.id,
                        relatedElement: relationshipDetail.element
                    }));
                    const relatedElements = relations.map(relation => relation.relatedElement);
                    const state: SmartReferenceListFieldState = {
                        originalRelations: relations,
                        relatedElements: relatedElements,
                        elements: filteredElements
                    };
                    return {
                        [smartReferenceField.referenceDefinitionId]: state
                    }
                });
    }

    validate(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, string> {
        const smartReferenceField = component as SmartReferenceListField;

        const referenceDefinition: ReferenceDefinitionDto = dataContext.referenceDefinitions[smartReferenceField.referenceDefinitionId];
        if (!referenceDefinition) {
            return {};
        }

        if (referenceDefinition.required) {
            const state: SmartReferenceListFieldState = stateContext.values[smartReferenceField.referenceDefinitionId];
            if (state.relatedElements.length < 1) {
                return {
                    [smartReferenceField.referenceDefinitionId]: CommonTranslationKey.FILL_OUT_THIS_FIELD
                };
            }
        }

        return {};
    }

    renderComponent({component, dataContext, stateContext, renderingContext}: UiComponentRenderingProps): ReactElement {
        const smartReferenceField = component as SmartReferenceListField;

        const referenceDefinition: ReferenceDefinitionDto = dataContext.referenceDefinitions[smartReferenceField.referenceDefinitionId];
        if (!referenceDefinition) {
            return (
                    <ErrorFieldPlaceholder key={smartReferenceField.id} error={t(StereotypesTranslationKey.UNKNOWN_REFERENCE)} />
            );
        }

        const state: SmartReferenceListFieldState = stateContext.values[smartReferenceField.referenceDefinitionId];
        if (!state) {
            return <Typography variant={"h5"}><Skeleton variant={"text"}/></Typography>;
        }

        const value: ElementDto[] = state.relatedElements ?? [];
        const elementOptions = state.elements ?? [];
        const error = stateContext.errors[smartReferenceField.referenceDefinitionId];
        const readOnly = smartReferenceField.readOnly || !stateContext.editMode;

        function onChange(newValue: ElementDto[]) {
            stateContext.onValuesChange({
                [smartReferenceField.referenceDefinitionId]: {
                    ...state,
                    relatedElements: newValue
                }
            });
        }

        return (
                <MultiSelectField
                        id={smartReferenceField.id}
                        key={smartReferenceField.id}
                        label={referenceDefinition.name}
                        values={value}
                        onChange={onChange}
                        options={elementOptions}
                        getRenderLabel={option => option?.name ?? ""}
                        isOptionEqualToValue={(option, value) => option.identifier === value?.identifier}
                        errorMessage={error ? t(error) : undefined}
                        required={referenceDefinition.required}
                        readOnly={readOnly}
                        limitTags={-1} />
        );
    }

    save(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext): Promise<void>[] {
        const smartReferenceField = component as SmartReferenceListField;

        const referenceDefinition: ReferenceDefinitionDto = dataContext.referenceDefinitions[smartReferenceField.referenceDefinitionId];
        if (!referenceDefinition) {
            return [];
        }

        const state: SmartReferenceListFieldState = stateContext.values[smartReferenceField.referenceDefinitionId];
        if (!state) {
            return [];
        }

        const originalRelations = state.originalRelations;
        const relatedElements = state.relatedElements;
        const relatedElementIds = new Set<string>(relatedElements.map(element => element.identifier));

        const processedRelatedElementIds = new Set<string>();

        const promises: Promise<void>[] = [];

        const currentRelations: RelationData[] = [];
        for (const relation of originalRelations) {
            if (relatedElementIds.has(relation.relatedElement.identifier)) {
                currentRelations.push(relation);
            } else {
                promises.push(this.relationshipApiClient.deleteRelationship(relation.relationshipId));
            }
            processedRelatedElementIds.add(relation.relatedElement.identifier);
        }

        for (const relatedElement of relatedElements) {
            if (!processedRelatedElementIds.has(relatedElement.identifier)) {
                const relationship = SmartReferenceSelectFieldPlugin.buildRelationship(dataContext.element, relatedElement, referenceDefinition);
                promises.push(this.relationshipApiClient.createRelationship(relationship));
                currentRelations.push({
                    relationshipId: relationship.identifier!,
                    relatedElement: relatedElement
                });
            }
        }

        state.originalRelations = currentRelations;

        return promises;
    }

}
